import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  IconButton,
  Typography,
  Chip,
  Box,
  Divider,
  alpha,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { BsChatLeftText } from 'react-icons/bs';
import { IoStar } from 'react-icons/io5';
import { TbMessageCircle } from 'react-icons/tb';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PauseIcon from '@mui/icons-material/Pause';
import ArchiveIcon from '@mui/icons-material/Archive';
import CircularProgress from '@mui/material/CircularProgress';

const COLORS = {
  primary: '#2196f3',
  success: '#4caf50',
  warning: '#ffc107',
  error: '#f44336',
  purple: '#673ab7',
  background: '#f7f9fc',
  lightBorder: '#e3f2fd',
  hoverRow: alpha('#2196f3', 0.08),
};

const SURVEY_TYPES = {
  rating: {
    icon: IoStar,
    label: 'Calificación',
  },
  text: {
    icon: TbMessageCircle,
    label: 'Texto',
  },
};

const STATUS_CONFIG = {
  active: {
    label: 'Activo',
    color: COLORS.success,
    bg: alpha(COLORS.success, 0.1),
    border: `1px solid ${alpha(COLORS.success, 0.3)}`,
    icon: PlayArrowIcon,
  },
  inactive: {
    label: 'Inactivo',
    color: '#757575',
    bg: alpha('#757575', 0.08),
    border: `1px solid ${alpha('#757575', 0.15)}`,
    icon: StopIcon,
  },
  paused: {
    label: 'Pausado',
    color: COLORS.warning,
    bg: alpha(COLORS.warning, 0.1),
    border: `1px solid ${alpha(COLORS.warning, 0.3)}`,
    icon: PauseIcon,
  },
  deleted: {
    label: 'Eliminado',
    color: COLORS.error,
    bg: alpha(COLORS.error, 0.1),
    border: `1px solid ${alpha(COLORS.error, 0.3)}`,
    icon: DeleteIcon,
  },
  archived: {
    label: 'Archivado',
    color: COLORS.purple,
    bg: alpha(COLORS.purple, 0.08),
    border: `1px solid ${alpha(COLORS.purple, 0.15)}`,
    icon: ArchiveIcon,
  },
  default: {
    label: 'Desconocido',
    color: '#757575',
    bg: alpha('#757575', 0.08),
    border: `1px solid ${alpha('#757575', 0.15)}`,
    icon: HelpOutlineIcon,
  },
};

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  boxShadow:
    '0px 2px 4px -1px rgba(0,0,0,0.05), 0px 1px 10px 0px rgba(0,0,0,0.025)',
  border: `1px solid ${COLORS.lightBorder}`,
  transition: 'all 0.2s ease-in-out',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    boxShadow:
      '0px 4px 8px -1px rgba(0,0,0,0.1), 0px 2px 15px 0px rgba(0,0,0,0.05)',
    transform: 'translateY(-2px)',
  },
}));

const getStatusProps = status => {
  return STATUS_CONFIG[status] || STATUS_CONFIG.default;
};

const getTypeConfig = type => {
  return SURVEY_TYPES[type] || SURVEY_TYPES.text;
};

const SurveyHeader = ({ survey }) => {
  const statusConfig = getStatusProps(survey.status);
  const StatusIcon = statusConfig.icon;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: 2,
      }}>
      {/* Título de la encuesta */}
      <Typography
        variant="h6"
        fontWeight={600}
        color="#424242"
        sx={{ flex: 1 }}>
        {survey.title}
      </Typography>

      {/* Estado de la encuesta */}
      <Chip
        icon={<StatusIcon />}
        label={statusConfig.label}
        size="small"
        sx={{
          backgroundColor: statusConfig.bg,
          color: statusConfig.color,
          border: statusConfig.border,
          fontWeight: 600,
        }}
      />
    </Box>
  );
};

const SurveyChips = ({ survey, onViewResponses, isDisabled }) => {
  const typeConfig = getTypeConfig(survey.type);
  const TypeIcon = typeConfig.icon;

  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
      {/* Tipo de encuesta */}
      <Chip
        icon={<TypeIcon size={20} />}
        label={typeConfig.label}
        size="small"
        variant="outlined"
      />

      {/* Ver respuestas de usuarios */}
      <Tooltip title="Ver respuestas de usuarios" arrow placement="top">
        <span>
          <Chip
            icon={<BsChatLeftText />}
            label={`${survey.responses} respuestas`}
            size="small"
            variant="outlined"
            onClick={() => !isDisabled && onViewResponses(survey)}
            disabled={isDisabled}
            sx={{
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              '&:hover': {
                backgroundColor: isDisabled
                  ? 'transparent'
                  : alpha(COLORS.primary, 0.08),
                borderColor: isDisabled ? 'inherit' : COLORS.primary,
              },
            }}
          />
        </span>
      </Tooltip>

      {/* Escala de calificación */}
      {survey.type === 'rating' && (
        <Chip
          icon={<IoStar />}
          label={`1-${survey.max_rating} estrellas`}
          size="small"
          variant="outlined"
        />
      )}
    </Box>
  );
};

const ActionButton = ({
  icon: Icon,
  onClick,
  tooltip,
  color,
  disabled,
  loading = false,
  loadingIcon: LoadingIcon = CircularProgress,
}) => (
  <Tooltip title={tooltip} arrow placement="top">
    <span>
      <IconButton
        size="small"
        onClick={onClick}
        sx={{ color }}
        disabled={disabled}>
        {loading ? <LoadingIcon size={20} /> : <Icon />}
      </IconButton>
    </span>
  </Tooltip>
);

const SurveyActions = ({
  survey,
  onEdit,
  onDelete,
  onToggleStatus,
  onPreview,
  deleting,
  toggling,
  disabled,
}) => {
  const isDisabled = disabled || deleting || toggling;
  const isActive = survey.status === 'active';

  return (
    <CardActions sx={{ p: 2, gap: 1 }}>
      {/* Vista previa de la encuesta */}
      <ActionButton
        icon={VisibilityIcon}
        onClick={() => onPreview(survey)}
        tooltip="Ver vista previa de la encuesta"
        color={COLORS.purple}
        disabled={isDisabled}
      />

      {/* Editar encuesta */}
      <ActionButton
        icon={EditIcon}
        onClick={() => onEdit(survey)}
        tooltip="Editar encuesta"
        color={COLORS.primary}
        disabled={isDisabled}
      />

      {/* Activar/pausar encuesta */}
      <ActionButton
        icon={isActive ? StopIcon : PlayArrowIcon}
        onClick={() => onToggleStatus(survey.id)}
        tooltip={
          isActive
            ? 'Pausar encuesta'
            : 'Activar encuesta (pausará la encuesta actual)'
        }
        color={isActive ? COLORS.error : COLORS.success}
        disabled={isDisabled}
        loading={toggling}
      />

      {/* Eliminar encuesta */}
      <ActionButton
        icon={DeleteIcon}
        onClick={() => onDelete(survey.id)}
        tooltip="Eliminar encuesta"
        color={COLORS.error}
        disabled={isDisabled}
        loading={deleting}
      />
    </CardActions>
  );
};

const SurveyCard = ({
  survey,
  onEdit,
  onDelete,
  onToggleStatus,
  onPreview,
  onViewResponses,
  deleting = false,
  toggling = false,
  disabled = false,
}) => {
  const isDisabled = disabled || deleting || toggling;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <StyledCard>
      <CardContent sx={{ flexGrow: 1 }}>
        <SurveyHeader survey={survey} />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {survey.question}
        </Typography>

        <SurveyChips
          survey={survey}
          onViewResponses={onViewResponses}
          isDisabled={isDisabled}
        />

        <Typography variant="caption" color="text.secondary">
          Última modificación: {formatDate(survey.updated_at)}
        </Typography>
      </CardContent>

      <Divider />

      <SurveyActions
        survey={survey}
        onEdit={onEdit}
        onDelete={onDelete}
        onToggleStatus={onToggleStatus}
        onPreview={onPreview}
        deleting={deleting}
        toggling={toggling}
        disabled={disabled}
      />
    </StyledCard>
  );
};

export default SurveyCard;
